import { HOSTING, HOSTING_AVAILABILITY, type HostingPlan, type Tier } from '../lib/quoteEstimator';

/**
 * Presentation data for /plans. Prices come ONLY from HOSTING in quoteEstimator
 * (the 17 approved base rates); the "selected when" strings restate the plan
 * selection rules implemented in selectHosting() — nothing here sets a price.
 */
export interface HostingCategory {
  id: string;
  /** Short label for tabs. */
  short: string;
  name: string;
  /** Development tier whose plan family this is (used only to call the shared engine). */
  engineTier: Tier;
  /** Matching development tiers, for cross-reference copy only. */
  devTiers: string;
  summary: string;
  /** Plain-language "what you run" examples (no development jargon). */
  runs: string;
  plans: { id: HostingPlan; label: string; when: string }[];
  /** Whether advanced-panel and AI options exist for this family (T4+ families). */
  advancedOptions: boolean;
}

export const HOSTING_CATEGORIES: HostingCategory[] = [
  {
    id: 'single',
    short: 'Single page',
    name: 'Single-page sites',
    engineTier: 'T1',
    devTiers: 'T1',
    summary: 'One custom page: a landing page, portfolio, or public information.',
    runs: 'Landing pages, basic portfolios, promos, simple public info.',
    advancedOptions: false,
    plans: [
      { id: 'H1a', label: 'Standard', when: 'Default for single-page sites' },
      { id: 'H1b', label: 'Managed content or busier', when: 'A standard content-management interface, or high traffic' },
    ],
  },
  {
    id: 'multi',
    short: 'Multi-page',
    name: 'Multi-page sites',
    engineTier: 'T2',
    devTiers: 'T2',
    summary: 'A multi-page informational presence for a small business.',
    runs: 'Restaurants, service businesses, company sites, product showcases.',
    advancedOptions: false,
    plans: [
      { id: 'H2a', label: 'Standard', when: 'Default for multi-page sites' },
      { id: 'H2b', label: 'Managed content or busier', when: 'A standard content-management interface, or high traffic' },
    ],
  },
  {
    id: 'frontend',
    short: 'Advanced front end',
    name: 'Advanced front-end and integrated sites',
    engineTier: 'T3A',
    devTiers: 'T3A and T3B',
    summary: 'Graphics-, motion-, or third-party-integration-heavy sites without a custom backend.',
    runs: 'Animation-rich showcases; sites using an external ordering, booking, or checkout service.',
    advancedOptions: false,
    plans: [
      { id: 'H3a', label: 'Standard', when: 'Default for advanced front-end sites' },
      { id: 'H3b', label: 'Managed content or busier', when: 'A standard content-management interface, or high traffic' },
    ],
  },
  {
    id: 'backend',
    short: 'Backend app',
    name: 'Backend applications (no database)',
    engineTier: 'T4A',
    devTiers: 'T4A',
    summary: 'Custom APIs and server-side processing without a significant database.',
    runs: 'Tools, calculators, workflow services, integration-heavy apps.',
    advancedOptions: true,
    plans: [
      { id: 'H4a1', label: 'Standard', when: 'Default for backend applications' },
      { id: 'H4a2', label: 'Managed content', when: 'A standard content-management interface' },
      { id: 'H4a3', label: 'Advanced, AI, or busier', when: 'An advanced management interface, AI self-healing, or high traffic' },
    ],
  },
  {
    id: 'database',
    short: 'Database app',
    name: 'Database-backed applications',
    engineTier: 'T4B',
    devTiers: 'T4B',
    summary: 'Persistent records, accounts, and authentication.',
    runs: 'Booking systems, client portals, record-keeping apps.',
    advancedOptions: true,
    plans: [
      { id: 'H4b1', label: 'Standard', when: 'Default for database-backed applications' },
      { id: 'H4b2', label: 'Managed content', when: 'A standard content-management interface' },
      { id: 'H4b3', label: 'Advanced or busier', when: 'An advanced management interface, or high traffic' },
      { id: 'H4b4', label: 'AI self-healing', when: 'AI self-healing' },
    ],
  },
  {
    id: 'reliability',
    short: 'High reliability',
    name: 'High-reliability and sensitive-data platforms',
    engineTier: 'T5',
    devTiers: 'T5',
    summary: 'Elevated reliability, redundancy, and sensitive-data responsibility.',
    runs: 'Regulated, high-traffic, or multi-system platforms.',
    advancedOptions: true,
    plans: [
      { id: 'H5a', label: 'Standard', when: 'Default for high-reliability platforms' },
      { id: 'H5b', label: 'Managed content', when: 'A standard content-management interface' },
      { id: 'H5c', label: 'Advanced or busier', when: 'An advanced management interface, or high traffic' },
      { id: 'H5d', label: 'AI self-healing', when: 'AI self-healing' },
    ],
  },
];

export const PLAN_COUNT = HOSTING_CATEGORIES.reduce((n, c) => n + c.plans.length, 0);

/** True when at least one plan in the category is currently offered. */
export const isCategoryAvailable = (category: HostingCategory): boolean =>
  category.plans.some((plan) => HOSTING_AVAILABILITY[plan.id]);

/** Lowest and highest approved monthly base rates. */
export const HOSTING_PRICE_MIN = Math.min(...Object.values(HOSTING));
export const HOSTING_PRICE_MAX = Math.max(...Object.values(HOSTING));

/** Highest approved rate among plans that are currently offered. */
export const AVAILABLE_HOSTING_PRICE_MAX = Math.max(
  ...HOSTING_CATEGORIES.flatMap((category) =>
    category.plans.filter((plan) => HOSTING_AVAILABILITY[plan.id]).map((plan) => HOSTING[plan.id]),
  ),
);

export const planPrice = (id: HostingPlan) => HOSTING[id];

/**
 * Directional industry comparison only — not a FairCorp price or quote.
 * Monthly ranges are planning benchmarks for the closest hosting category.
 */
export const HOSTING_INDUSTRY_RANGES: Record<HostingPlan, string> = {
  H1a: '$3–$15/mo', H1b: '$3–$15/mo',
  H2a: '$10–$35/mo', H2b: '$10–$35/mo',
  H3a: '$25–$75/mo', H3b: '$25–$75/mo',
  H4a1: '$75–$200/mo', H4a2: '$75–$200/mo', H4a3: '$75–$200/mo',
  H4b1: '$150–$350/mo', H4b2: '$150–$350/mo', H4b3: '$150–$350/mo', H4b4: '$150–$350/mo',
  H5a: '$300–$1,000+/mo', H5b: '$300–$1,000+/mo', H5c: '$300–$1,000+/mo', H5d: '$300–$1,000+/mo',
};
